import calendarsDataProcessor from '@/utils/calendarsDataProcessor'
import LegacyClient from '@/clients/legacyClient'
import * as types from './mutation-types'
import { CustomError } from '@/utils/helpers'

export const getPracticeCalendarInfo = async ({ state, commit, dispatch }, { practiceId, track }) => {
  const client = new LegacyClient()
  const { status, message } = await client.getPracticeCalendarInfo({ practiceId, track })
  if (status !== 'success') {
    throw new CustomError(message, {
      status
    })
  }
  dispatch('initCalendars', { allDocLocs: message.allDocLocs, allLocs: message.allLocs, calendarsInfo: message.calendars, apptPhoneFormatted: message.apptPhoneFormatted, apptPhone: message.apptPhone })
  const allDocs = Array.isArray(message.allDocLocs) ? message.allDocLocs.map(el => el.doctor_id) : Object.keys(message.allDocLocs || {}).map(id => +id)
  commit(types.UPDATE_INSURANCES, {
    allDocsIns: message.allDocsIns,
    allDocs
  })

  // additional logic for practices with only one provider
  commit(types.UPDATE_INSURANCES_FOR_PRACTICES_WITH_ONE_PROVIDER, { insurances: (message.insurances || []), allDocs })

  commit(types.UPDATE_REASONS, message.reasons)
  commit(types.SET_APPOINTMENT_TYPES, message?.appointmentTypesInfo?.appointmentTypes ?? [])
  commit(types.SET_APPOINTMENT_TYPES_ENABLED, message?.appointmentTypesInfo?.enabled ?? false)
}

export const getCalendarInfo = async ({ state, getters, commit }, { providerId, locationId }) => {
  const client = new LegacyClient()
  const calendarIndex = `${providerId}_${locationId}`
  // queue used for processing api response in order they started
  commit(types.ADD_ITEM_TO_A_QUEUE, calendarIndex)

  const calendarResponse = await client.getCalendarInfo({ locationId, providerId })
  const { status, message } = calendarResponse
  const thisItemInQueue = getters.getQueueItem(calendarIndex)
  const responseCanBeProcessed = getters.getResponseCanBeProcessed(calendarIndex)
  // promise was added to avoid "maximum call stack error" for a long queues
  return new Promise((resolve) => {
    const responseProcessing = async () => {
      const moveToNextItem = async () => {
        commit(types.SET_QUEUE_ITEM_IS_PROCESSED, calendarIndex)
        const nextItem = state.requestQueue[thisItemInQueue.nextItemIndex]
        if (nextItem && !nextItem.processed && nextItem.precessCallback) {
          setTimeout(() => {
            nextItem.precessCallback()
          }, 0)
        }
        resolve()
      }
      /*
        base calendar info which we get from base practice endpoint (legacy/getPracticeCalendarInfo)
        need to get info to display "call office" message if calendar has no availability - nocalendar status
        if calendar not exist in this array - this calendar was not created
       */
      const calendarForThisCalendarIndex = state.initCalendarsInfo.find(calendar => {
        return (!calendar.doctor_id || +calendar.doctor_id === +providerId) && (!calendar.location_id || +calendar.location_id === +locationId)
      })
      const calendarExist = !!calendarForThisCalendarIndex

      const saveCalendarId = () => {
        calendarExist && commit(types.SAVE_CALENDAR_ID, calendarForThisCalendarIndex.id)
      }
      if (status !== 'success' || Object.keys(state.calendars).find(calendar => state.calendars[calendar].calendarId === message.calendars[0].id)) {
        // first "if" shouldn't happen, but leave for unexpected scenario
        if (!calendarExist || getters.thisCalendarIdAlreadySaved(calendarForThisCalendarIndex.id)) {
          commit(types.REMOVE_CALENDAR, calendarIndex)
        } else if (status !== 'success') {
          commit(types.UPDATE_CALENDAR_PHONE_NUMBERS, {
            calendarIndex,
            phone: calendarResponse.phone,
            formattedPhone: calendarResponse.formattedPhone
          })
        }
        saveCalendarId()
        await moveToNextItem()
        return
      }
      saveCalendarId()
      // @TODO how can we get the practice specialty? message.allSpecs?

      commit(types.UPDATE_TIME_BLOCKS, { calendarIndex, timeBlocks: message.calendars[0].calendar_blocks })
      // no need to update all CalendarDetails only phone numbers need to be updated
      commit(types.UPDATE_CALENDAR_PHONE_NUMBERS, {
        calendarIndex,
        phone: calendarResponse.message.apptPhone,
        formattedPhone: calendarResponse.message.apptPhoneFormatted
      })
      commit(types.UPDATE_CALENDAR_INSURANCES, { calendarIndex, insurances: message.insurances })
      commit(types.UPDATE_INSURANCES_WITH_CALENDAR_INDEX, { calendarIndex, insurancesIdsList: (message.insurances || []).map(el => el.id) })
      commit(types.UPDATE_CALENDAR_REASONS, { calendarIndex, reasons: message.reasons })
      await moveToNextItem()
    }

    if (responseCanBeProcessed) {
      responseProcessing()
    } else {
      commit(types.SET_RESPONSE_CALLBACK_TO_ITEM_IN_A_QUEUE, {
        calendarIndex,
        precessCallback: responseProcessing
      })
    }
  })
}

export const saveAppointment = ({ state, commit }, data) => {
  const client = new LegacyClient()

  const dataToSend = {
    calendar_id: data.calendarId,
    calendar_block_id: data.calendarBlockId,
    doctor_id: data.doctorId,
    location_id: data.locationId,
    is_telehealth: data.isTelehealth || 'no',
    newpatient: data.newPatient ? 'yes' : 'no',
    firstname: data.firstName,
    lastname: data.lastName,
    email: data.email,
    phone: data.phone,
    date_of_birth: data.dateOfBirth,
    sex: data.sex,
    agree_sms_terms: data.agreeSmsTerms ? 'yes' : 'no',
    reason: data.reason,
    insurance_provider_id: data.insuranceProviderId,
    insurance_id_number: data.insuranceIdNumber,
    insurance_group_number: data.insuranceGroupNumber,
    insurance_phone: data.insurancePhone || null,
    comment: data.comment
  }

  if (state.appointmentTypesEnabled && data.appointmentTypeUuid) {
    dataToSend.appointment_type_uuid = data.appointmentTypeUuid
  }

  // add origin to payload only if it exist
  if (data.origin) {
    dataToSend.origin = data.origin
  }

  let operation
  if (state?.createdAppointment?.appointmentId) {
    dataToSend.id = state.createdAppointment.appointmentId
    // Remove fields not allowed on UPDATE endpoint
    delete dataToSend.agree_sms_terms
    delete dataToSend.is_telehealth
    delete dataToSend.newpatient
    operation = client.updateAppointment(dataToSend)
  } else {
    operation = client.makeAppointment(dataToSend)
  }

  return operation
    .then((response) => {
      if (response.status !== 'success') {
        const e = new CustomError(response.message, {}, 'SaveAppointmentError')
        console.error(response.code || response.message)
        throw e
      }
      if (response?.appointment_id) {
        // Only update store when new information has been sent over.
        commit(types.UPDATE_CREATED_APPOINTMENT, response)
      }
    })
    .catch((error) => {
      throw error
    })
}

export const resetCreatedAppointment = ({ commit }) => {
  commit(types.RESET_CREATED_APPOINTMENT)
}

export const loadGoogleMapsApiKey = async ({ commit }, { practiceId }) => {
  const client = new LegacyClient()
  try {
    const apiKey = await client.getGoogleMapsApiKey({ practiceId })
    if (apiKey) {
      commit(types.SET_GOOGLE_MAPS_API_KEY, apiKey)
    }
  } catch (e) {
    // swallow the error. we do not want to warn the user in this case
  }
}

export const initCalendars = ({ commit }, { allDocLocs, allLocs, calendarsInfo = [], apptPhoneFormatted = '', apptPhone = '' }) => {
  const {
    standardizedDocLocs,
    initCalendarsData,
    calendars,
    calendarIndexToLocationAndProviderMap,
    calendarIdToIndexMap,
    allPracticeLocations,
    allPracticeProviders
  } = calendarsDataProcessor(calendarsInfo, allDocLocs, allLocs, apptPhoneFormatted, apptPhone)

  commit(types.SAVE_ALL_DOC_LOCS, standardizedDocLocs)
  commit(types.SAVE_INIT_CALENDARS_INFO, initCalendarsData)
  commit(types.SAVE_CALENDARS, calendars)
  commit(types.SAVE_LOCATIONS, allPracticeLocations)
  commit(types.SAVE_PROVIDERS, allPracticeProviders)
  commit(types.SAVE_CALENDAR_INDEX_TO_LOCATION_AND_PROVIDER_MAP, calendarIndexToLocationAndProviderMap)
  commit(types.SAVE_CALENDAR_ID_TO_INDEX_MAP, calendarIdToIndexMap)
}
